import { useEffect, useRef, useState } from 'react'
import { Search, Loader2, Phone, PhoneOff, Users } from 'lucide-react'
import { AvatarStateRing } from './AvatarStateRing'
import { useContactSearch } from '@/hooks/useContactSearch'
import type { ApiContact } from '@/lib/apiTypes'
import type { MakeCallMeta } from '@/stores/callStore'

// ─── Types & helpers ─────────────────────────────────────────────────────────

interface ContactPickerTabProps {
  onCall: (number: string, meta?: MakeCallMeta) => void
}

const looksLikePhone = (q: string) => /^[\d+\-()\s]{3,}$/.test(q.trim())

function contactFullName(c: ApiContact): string {
  return `${c.firstName} ${c.lastName}`.trim()
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Contacts tab for the softphone panel.
 * MIDNIGHT Input-style search + MobileList-style rows.
 * Typeahead powered by useContactSearch; keyboard navigation local.
 */
export function ContactPickerTab({ onCall }: ContactPickerTabProps) {
  const [query, setQuery] = useState('')
  const [highlightIndex, setHighlightIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { results, loading, error } = useContactSearch({ query, take: 5 })

  // Auto-focus the input when the tab mounts
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Reset highlight any time the results change
  useEffect(() => {
    setHighlightIndex(results.length > 0 ? 0 : -1)
  }, [results])

  // Scroll the highlighted row into view
  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return
    const items = listRef.current.querySelectorAll('[data-contact-row]')
    items[highlightIndex]?.scrollIntoView({ block: 'nearest' })
  }, [highlightIndex])

  const trimmed = query.trim()
  const canDialRaw = looksLikePhone(trimmed)

  // ── Selection ──────────────────────────────────────────────────────────────

  function dialContact(contact: ApiContact) {
    if (!contact.phone) return
    onCall(contact.phone, {
      name: contactFullName(contact),
      contactId: contact.id,
    })
  }

  function dialRaw(number: string) {
    onCall(number)
  }

  function handleEnter() {
    if (highlightIndex >= 0 && highlightIndex < results.length) {
      const contact = results[highlightIndex]
      if (contact.phone) dialContact(contact)
    } else if (results.length === 0 && canDialRaw) {
      dialRaw(trimmed)
    } else if (results.length > 0 && results[0]?.phone) {
      const firstDialable = results.find((c) => c.phone)
      if (firstDialable) dialContact(firstDialable)
    }
  }

  // ── Keyboard nav ───────────────────────────────────────────────────────────

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (results.length === 0 && e.key === 'Enter') {
      e.preventDefault()
      handleEnter()
      return
    }
    if (results.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1))
        break
      case 'Enter':
        e.preventDefault()
        handleEnter()
        break
    }
  }

  const showEmptyState =
    !loading && trimmed.length >= 2 && results.length === 0 && !error

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px 12px', flex: 1, minHeight: 0 }}>
      {/* MIDNIGHT Search input */}
      <div style={{ position: 'relative' }}>
        <Search
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 15,
            height: 15,
            color: 'var(--midnight-text3)',
            pointerEvents: 'none',
          }}
        />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search contacts…"
          autoComplete="off"
          spellCheck={false}
          style={{
            width: '100%',
            height: 44,
            paddingLeft: 40,
            paddingRight: loading ? 40 : 12,
            borderRadius: 'var(--r-md)',
            border: '0.5px solid var(--midnight-border)',
            background: 'var(--midnight-surface-2, var(--surface-1))',
            color: 'rgb(var(--fg-rgb))',
            // 16px floor — iOS Safari auto-zooms <16px (FND-M-007).
            fontSize: 16,
            boxSizing: 'border-box',
            transition: 'border-color 0.2s ease, background 0.2s ease',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'rgb(var(--fg-rgb) / 0.25)'
            e.currentTarget.style.background = 'color-mix(in srgb, rgb(var(--fg-rgb)) 12%, rgb(var(--bg-rgb)))'
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'rgb(var(--fg-rgb) / 0.08)'
            e.currentTarget.style.background = 'var(--midnight-surface-2, var(--surface-1))'
          }}
        />
        {loading && (
          <Loader2
            style={{
              position: 'absolute',
              right: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 15,
              height: 15,
              color: 'var(--midnight-text3)',
              animation: 'spin 1s linear infinite',
            }}
          />
        )}
      </div>

      {/* Results list — fills remaining vertical space inside the PhoneSheet body. */}
      <div
        ref={listRef}
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginLeft: -4, marginRight: -4, borderRadius: 'var(--r-sm)' }}
      >
        {/* < 2 chars: gentle hint */}
        {trimmed.length < 2 && (
          <p
            style={{
              textAlign: 'center',
              padding: '24px 12px',
              fontSize: 13,
              color: 'var(--midnight-text3)',
            }}
          >
            Type a name or phone number.
          </p>
        )}

        {/* Error */}
        {error && trimmed.length >= 2 && (
          <p
            style={{
              textAlign: 'center',
              padding: '16px 12px',
              fontSize: 13,
              color: 'var(--midnight-blush)',
            }}
          >
            Couldn&apos;t reach the server. Try again.
          </p>
        )}

        {/* Results */}
        {results.map((contact, idx) => {
          const hasPhone = !!contact.phone
          const fullName = contactFullName(contact)
          const rowHighlighted = idx === highlightIndex
          return (
            <button
              key={contact.id}
              data-contact-row
              type="button"
              onClick={() => hasPhone && dialContact(contact)}
              onMouseEnter={() => setHighlightIndex(idx)}
              disabled={!hasPhone}
              title={hasPhone ? `Call ${fullName}` : 'No phone on file.'}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 8px',
                borderRadius: 'var(--r-sm)',
                border: 'none',
                background: rowHighlighted
                  ? 'rgb(var(--fg-rgb) / 0.08)'
                  : 'transparent',
                cursor: hasPhone ? 'pointer' : 'not-allowed',
                opacity: hasPhone ? 1 : 0.5,
                textAlign: 'left',
                touchAction: 'manipulation',
                transition: 'background 0.15s ease',
              }}
            >
              <AvatarStateRing
                name={fullName}
                state={hasPhone ? 'neutral' : 'post-call'}
                size="xs"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'rgb(var(--fg-rgb))',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: 2,
                  }}
                >
                  {fullName}
                </p>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    color: 'var(--midnight-text3)',
                  }}
                >
                  {hasPhone ? (
                    <>
                      <Phone style={{ width: 11, height: 11, flexShrink: 0 }} />
                      <span style={{ fontVariantNumeric: 'tabular-nums', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {contact.phone}
                      </span>
                    </>
                  ) : (
                    <>
                      <PhoneOff style={{ width: 11, height: 11, flexShrink: 0 }} />
                      <span>No phone on file</span>
                    </>
                  )}
                </div>
              </div>
            </button>
          )
        })}

        {/* Empty state — inline (custom CTA icon, can't use shared EmptyState which forces a "+" icon) */}
        {showEmptyState && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              padding: '20px 12px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 9999,
                background: 'rgb(var(--fg-rgb) / 0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--midnight-text3)',
              }}
            >
              <Users style={{ width: 20, height: 20 }} />
            </div>
            <p style={{ fontSize: 14, color: 'rgb(var(--fg-rgb) / 0.85)', margin: 0, fontWeight: 600 }}>
              No matches for &ldquo;{trimmed}&rdquo;
            </p>
            <p style={{ fontSize: 12, color: 'var(--midnight-text3)', margin: 0 }}>
              {canDialRaw ? 'Dial the number anyway?' : 'Try a different name or phone number.'}
            </p>
            {canDialRaw && (
              <button
                type="button"
                onClick={() => dialRaw(trimmed)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 16px',
                  borderRadius: 'var(--r-pill)',
                  border: '0.5px solid transparent',
                  background: 'var(--accent)',
                  color: 'var(--accent-ink)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                  marginTop: 2,
                }}
              >
                <Phone style={{ width: 13, height: 13 }} />
                Dial {trimmed}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
